import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Button, Modal, Spin, Typography, Input, message } from 'antd';
import axios from 'axios';
import { AUDIO_UPLOAD_API, AUDIO_TRANSCRIPTION_API } from '../config';
import { knowledgeAPI } from '../services/knowledge-api';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { ensureMediaPermission, notifyMediaDenied } from '../utils/mediaPermission';

const { Title, Text } = Typography;
const { TextArea } = Input;

export interface VoiceInputModalHandle {
  start: () => void;
}

export interface VoiceInputModalProps {
  /** 清書結果テキスト（親が保持・スクロール制御等に使用） */
  tempContent: string;
  onTempContentChange: (content: string) => void;
  /** 「使用する」押下時に清書テキストを親へ渡す */
  onUse?: (text: string) => void;
  overlayZIndex?: number;
  modalZIndex?: number;
  wrapClassName?: string;
}

const VoiceInputModal = forwardRef<VoiceInputModalHandle, VoiceInputModalProps>(({
  tempContent,
  onTempContentChange,
  onUse,
  overlayZIndex = 1000,
  modalZIndex,
  wrapClassName,
}, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [volume, setVolume] = useState(0);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const startRecording = async () => {
    try {
      // 使用直前にマイク権限を確認する。未許可なら通知モーダルを表示し、録音へ進めない。
      const permitted = await ensureMediaPermission('microphone');
      if (!permitted) {
        setIsRecording(false);
        return;
      }

      onTempContentChange('');
      setVolume(0);
      setIsRecording(true);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Web Audio API セットアップ（リアルタイム音量計測）
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.fftSize);
      const THRESHOLD = 1.5;
      let speaking = false;

      const checkVoiceActivity = () => {
        analyser.getByteTimeDomainData(dataArray);
        let sumSq = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const x = dataArray[i] - 128;
          sumSq += x * x;
        }
        const rms = Math.sqrt(sumSq / dataArray.length);
        setVolume(Math.min(rms / 128, 1));
        if (rms > THRESHOLD) { if (!speaking) speaking = true; }
        else { if (speaking) speaking = false; }
      };

      const intervalId = setInterval(checkVoiceActivity, 100);

      try {
        let audioChunks: BlobPart[] = [];
        let options = {};
        const mimeTypes = ['audio/mpeg', 'audio/mp3', 'audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav', ''];
        for (const type of mimeTypes) {
          try {
            if (type && MediaRecorder.isTypeSupported(type)) { options = { mimeType: type }; break; }
          } catch {}
        }

        const recorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = recorder;

        recorder.addEventListener('dataavailable', (e: BlobEvent) => {
          if (e.data.size > 0) audioChunks.push(e.data);
        });

        recorder.addEventListener('stop', async () => {
          clearInterval(intervalId);
          audioCtx.close();
          stream.getTracks().forEach(track => track.stop());

          if (audioChunks.length === 0) {
            message.error('録音データが取得できませんでした。再度お試しください。');
            setIsRecording(false);
            setIsProcessing(false);
            return;
          }

          try {
            const blobMimeType = 'audio/mpeg';
            const audioBlob = new Blob(audioChunks, { type: blobMimeType });

            if (audioBlob.size === 0) {
              message.error('録音データが空です。もう一度お試しください。');
              setIsRecording(false);
              setIsProcessing(false);
              return;
            }

            const formData = new FormData();
            const fileName = `voice_${Date.now()}.mp3`;
            formData.append('audioFile', new File([audioBlob], fileName, { type: blobMimeType }));

            // 保存名に企業コード・ユーザーNoを埋め込むため送信（どちらもログインユーザーに紐づく値）
            const companyId = (await indexedDBStorage.getItem('companyId')) || '';
            let userNo = '';
            try {
              const userStr = await indexedDBStorage.getItem('user');
              if (userStr) userNo = JSON.parse(userStr).userno ?? '';
            } catch {}
            formData.append('companyid', String(companyId));
            formData.append('userno', String(userNo));

            // 自社紹介文・専門用語の取得と音声アップロードを並行実行
            const [uploadResponse, knowledge] = await Promise.all([
              axios.post(AUDIO_UPLOAD_API, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              }),
              knowledgeAPI.getKnowledgeList(),
            ]);

            if (uploadResponse.data.success) {
              const transcribeResponse = await axios.post(AUDIO_TRANSCRIPTION_API, {
                filename: uploadResponse.data.filename,
                introduction: knowledge.companyintroduction,
                wordlist: knowledge.knowledgelist,
              });

              if (transcribeResponse.data.success) {
                const transcription = transcribeResponse.data.data.transcription || '';
                const summary = transcribeResponse.data.data.summary || '';
                onTempContentChange(summary || transcription);
                setIsRecording(false);
                setIsProcessing(false);
                setConfirmVisible(true);
              } else {
                throw new Error(transcribeResponse.data.message || 'AI清書処理に失敗しました');
              }
            } else {
              throw new Error(uploadResponse.data.message || '音声アップロードに失敗しました');
            }
          } catch (error: any) {
            message.error(error.message || 'AI清書処理に失敗しました');
            setIsRecording(false);
            setIsProcessing(false);
            if (process.env.NODE_ENV === 'development') {
              onTempContentChange('先方営業部の鈴木様と打ち合わせを行いました。製品Bについて詳細な説明を求められたため、次回までにカタログと見積もりを準備することになりました。');
              setConfirmVisible(true);
            }
          }
        });

        recorder.addEventListener('error', () => {
          message.error('録音中にエラーが発生しました');
          setIsRecording(false);
        });

        recorder.start(200);

        const dataInterval = setInterval(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.requestData();
          } else {
            clearInterval(dataInterval);
          }
        }, 1000);
      } catch {
        message.error('録音の初期化に失敗しました。ブラウザの互換性を確認してください。');
        setIsRecording(false);
        setIsProcessing(false);
      }
    } catch {
      // 権限拒否等でマイクにアクセスできなかった場合の案内（ブラウザ時のみモーダル、アプリ内はネイティブ側で表示済み）
      notifyMediaDenied('microphone');
      setIsRecording(false);
      setIsProcessing(false);
    }
  };

  const stopRecording = () => {
    try {
      setIsProcessing(true);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      } else {
        console.warn('MediaRecorder was not in recording state');
      }
    } catch {
      message.error('録音の終了に失敗しました');
      setIsRecording(false);
      setIsProcessing(false);
    }
  };

  useImperativeHandle(ref, () => ({ start: startRecording }));

  useEffect(() => {
    if (confirmVisible && !isRecording) {
      document.body.classList.add('voice-confirm-modal-open');
    } else {
      document.body.classList.remove('voice-confirm-modal-open');
    }
    return () => {
      document.body.classList.remove('voice-confirm-modal-open');
    };
  }, [confirmVisible, isRecording]);

  const handleCancel = () => {
    setConfirmVisible(false);
    onTempContentChange('');
  };

  const handleUse = () => {
    onUse?.(tempContent);
    setConfirmVisible(false);
    onTempContentChange('');
  };

  const footer = [
    <Button key="cancel" onClick={handleCancel}>キャンセル</Button>,
    <Button key="use" type="primary" onClick={handleUse}>使用する</Button>,
  ];

  return (
    <>
      {/* 録音中オーバーレイ */}
      {isRecording && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: overlayZIndex,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          padding: '0 20px',
        }}>
          <div style={{
            backgroundColor: 'white', padding: '40px 20px', borderRadius: '12px',
            width: '80%', maxWidth: '400px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
          }}>
            <Title level={4} style={{ margin: 0, color: isProcessing ? '#1890ff' : '#ff4d4f' }}>
              {isProcessing ? 'AI清書処理中' : '録音中'}
            </Title>
            {isProcessing ? (
              <Spin size="large" />
            ) : (
              <div style={{ width: '100%', height: '24px', position: 'relative', backgroundColor: '#f0f0f0', margin: '10px 0' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: `${volume * 500}%`, backgroundColor: '#ff4d4f', transform: 'translateX(-50%)', transition: 'width 0.1s linear' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, right: '50%', width: `${volume * 500}%`, backgroundColor: '#ff4d4f', transform: 'translateX(50%)', transition: 'width 0.1s linear' }} />
              </div>
            )}
            {!isProcessing && (
              <Button type="primary" danger shape="round" size="large" onClick={stopRecording}
                style={{ minWidth: '140px', height: '54px', fontSize: '16px' }}>
                録音停止
              </Button>
            )}
          </div>
        </div>
      )}

      {/* AI清書結果確認モーダル */}
      {confirmVisible && !isRecording && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: (modalZIndex ?? 1000) - 1 }} />
          <Modal
            title="AI清書結果の確認"
            open={confirmVisible}
            onCancel={handleCancel}
            zIndex={modalZIndex}
            mask={false}
            wrapClassName={wrapClassName}
            footer={footer}
          >
          <div style={{ marginBottom: 16 }}>
            <Text>AI清書の結果です。内容を確認してください。</Text>
          </div>
          <TextArea
            rows={6}
            value={tempContent}
            onChange={(e) => onTempContentChange(e.target.value)}
            placeholder="AI清書結果がここに表示されます"
            style={{ marginBottom: 16 }}
          />
          </Modal>
        </>
      )}
    </>
  );
});

VoiceInputModal.displayName = 'VoiceInputModal';

export default VoiceInputModal;
